import { httpClient } from "../http-client";
import type { ApiResponse } from "../../../shared/types/api";

export type MailSenderProvider = "NAVER" | "DAUM" | "GOOGLE" | "OTHER";
export type MailPurpose = "MEMBER_ACCOUNT" | "ORDER_SHIPPING" | "ESTIMATE" | "ADMIN_NOTICE";

/** 발신 계정. 비밀번호는 서버가 내려주지 않는다 */
export interface MailSender {
  id: number;
  provider: MailSenderProvider;
  emailAddress: string;
  fromAddress: string | null;
  connectionVerifiedAt: string | null;
  lastFailureAt: string | null;
  lastFailureMessage: string | null;
}

/** 기능 묶음과 지정된 발신 계정. senderId 가 null 이면 기본 계정(주문 메일함 계정) */
export interface MailPurposeAssignment {
  purpose: MailPurpose;
  label: string;
  description: string;
  senderId: number | null;
}

export interface CreateMailSenderRequest {
  provider: MailSenderProvider;
  emailAddress: string;
  password: string;
  fromAddress?: string;
}

const unwrap = <T,>(data: ApiResponse<T>, fallbackMessage: string): T => {
  if (data.errorCode || data.data === undefined) {
    throw new Error(data.errorMsg || fallbackMessage);
  }
  return data.data;
};

/** 서버 오류 응답(4xx)의 errorMsg 를 꺼낸다 */
export const mailSenderErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: ApiResponse<unknown> } })?.response;
  return response?.data?.errorMsg || (error instanceof Error && error.message) || fallback;
};

class MailSenderApiService {
  async listSenders(companyId: number): Promise<MailSender[]> {
    const response = await httpClient.get<ApiResponse<MailSender[]>>(`/api/company/${companyId}/mail-senders`);
    return unwrap(response.data, "발신 계정을 불러오지 못했습니다.");
  }

  async addSender(companyId: number, request: CreateMailSenderRequest): Promise<MailSender> {
    const response = await httpClient.post<ApiResponse<MailSender>>(`/api/company/${companyId}/mail-senders`, request);
    return unwrap(response.data, "발신 계정을 추가하지 못했습니다.");
  }

  async deleteSender(companyId: number, senderId: number): Promise<void> {
    const response = await httpClient.delete<ApiResponse<unknown>>(`/api/company/${companyId}/mail-senders/${senderId}`);
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "발신 계정을 삭제하지 못했습니다.");
  }

  async listAssignments(companyId: number): Promise<MailPurposeAssignment[]> {
    const response = await httpClient.get<ApiResponse<MailPurposeAssignment[]>>(`/api/company/${companyId}/mail-purposes`);
    return unwrap(response.data, "기능별 발신 계정을 불러오지 못했습니다.");
  }

  async assign(companyId: number, purpose: MailPurpose, senderId: number | null): Promise<MailPurposeAssignment[]> {
    const response = await httpClient.put<ApiResponse<MailPurposeAssignment[]>>(
      `/api/company/${companyId}/mail-purposes/${purpose}`,
      { senderId },
    );
    return unwrap(response.data, "기능별 발신 계정을 저장하지 못했습니다.");
  }
}

export const mailSenderApiService = new MailSenderApiService();
